import { logger } from 'packages/logger';
import { API_ERRORS } from 'tradera/utils/api';
import { cache, CACHE_KEYS } from 'tradera/utils/cache';
import {
    openProfileMenu,
    closeModal,
    openNotificationOptinModal,
    logOut,
    setSystemMessages,
    setSystemBadges
} from './reducer';
import { getSystemMessages, getSystemBadges } from './api';
import {
    type SystemMessage,
    SystemMessageSeverity,
    type SystemMessageType
} from '../services/types/webapi-member-generated';
import type { AppDispatch, GetState } from 'tradera/state/configure-store';
import type {
    SystemBadgeType,
    SystemBadge
} from 'tradera/state/services/types/webapi-mytradera-generated';

/**
 * signIn
 * @param {object} [payload]
 * @param {string} payload.redirectUrl - where to send the user after login completes
 * @param {boolean} [payload.siteHeader] - Experiment: different behaviour for clicks in site header
 */
export const signIn =
    (payload?: { redirectUrl: string; siteHeader?: boolean }) =>
    (dispatch: AppDispatch) => {
        dispatch(openProfileMenu(payload?.redirectUrl));
        dispatch(closeModal());
    };

/**
 * Open a notification optin modal
 *
 * @param {boolean} type - type of notifications to show modal for
 * @returns {function(*, *): boolean}
 */
export const openNotificationOptinModalAction =
    (
        modalType: string,
        gaLabel: string,
        title: string,
        description: string,
        tagGroups: unknown[]
    ) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const { pushNotifications } = getState();
        if (!pushNotifications.enabled) {
            return;
        }
        if (
            pushNotifications.shownModals[
                modalType as keyof typeof pushNotifications.shownModals
            ] === true
        ) {
            return;
        }
        dispatch(
            openNotificationOptinModal({
                modalType,
                gaLabel,
                title,
                description,
                tagGroups
            })
        );
    };

export const logOutAction = () => async (dispatch: AppDispatch) => {
    cache.remove(CACHE_KEYS.SYSTEM_MESSAGES);
    cache.remove(CACHE_KEYS.SYSTEM_BADGES);

    // This is a hacky way to log out from Firebase even though the Firebase
    // app might not be loaded currently. We can use the firebase.auth().signOut()
    // method if inside Messaging and the Firebase app is loaded, but after
    // leaving Messaging we need to delete the database that holds auth information
    // to make sure the user is logged out.
    indexedDB.deleteDatabase('firebaseLocalStorageDb');

    dispatch(logOut());
};

export const fetchSystemMessages =
    () => async (dispatch: AppDispatch, getState: GetState) => {
        const {
            member: { isLoggedIn }
        } = getState();
        if (isLoggedIn) {
            try {
                let systemMessages = cache.read(CACHE_KEYS.SYSTEM_MESSAGES);
                if (!systemMessages?.messages) {
                    const fetchedMessages = await getSystemMessages();
                    if (fetchedMessages?.data?.items) {
                        systemMessages = {
                            messages: fetchedMessages.data.items.reduce(
                                (
                                    acc: Record<SystemMessageType, boolean>,
                                    message: SystemMessage
                                ) => ({
                                    ...acc,
                                    [message.type]: true
                                }),
                                {}
                            ),
                            notificationCount:
                                fetchedMessages.data.items.filter(
                                    (message: SystemMessage) =>
                                        message.severity ===
                                        SystemMessageSeverity.Warning
                                ).length
                        };
                        cache.write(CACHE_KEYS.SYSTEM_MESSAGES, systemMessages);
                    }
                }
                dispatch(setSystemMessages(systemMessages));
            } catch (error: unknown) {
                // Invalidate cache to fetch fresh again next action
                cache.remove(CACHE_KEYS.SYSTEM_MESSAGES);
                if (!(error instanceof Error)) {
                    logger(error);
                    return;
                }
                switch (error.message) {
                    case API_ERRORS.LOGGED_OUT:
                        await dispatch(logOutAction());
                        window.location.href = '/login/logout';
                        break;
                    case API_ERRORS.IGNORE_ME:
                    case API_ERRORS.CANCELLED:
                    case API_ERRORS.NETWORK:
                    case API_ERRORS.TIMEOUT:
                        break;
                    default:
                        logger(error);
                        break;
                }
            }
        }
    };

export const fetchSystemBadges =
    () => async (dispatch: AppDispatch, getState: GetState) => {
        const {
            member: { isLoggedIn }
        } = getState();
        if (!isLoggedIn) return;

        try {
            let systemBadges = cache.read(CACHE_KEYS.SYSTEM_BADGES);
            if (!systemBadges?.badges) {
                const fetchedBadges = await getSystemBadges();
                if (fetchedBadges?.data?.badges) {
                    const badgeMap = fetchedBadges.data.badges.reduce(
                        (
                            acc: Record<SystemBadgeType, SystemBadge>,
                            badge: SystemBadge
                        ) => ({
                            ...acc,
                            [badge.type]: badge
                        }),
                        {}
                    );
                    systemBadges = {
                        badges: badgeMap,
                        notificationCount: badgeMap.PayoutsActivity ? 1 : 0
                    };
                    cache.write(CACHE_KEYS.SYSTEM_BADGES, systemBadges);
                }
            }
            dispatch(setSystemBadges(systemBadges));
        } catch (error: unknown) {
            // Invalidate cache to fetch fresh again next action
            cache.remove(CACHE_KEYS.SYSTEM_BADGES);
            if (
                error instanceof Error &&
                error.message === API_ERRORS.LOGGED_OUT
            ) {
                await dispatch(logOutAction());
                window.location.href = '/login/logout';
            }
        }
    };

export const reloadSystemMessages = () => (dispatch: AppDispatch) => {
    cache.remove(CACHE_KEYS.SYSTEM_MESSAGES);
    dispatch(fetchSystemMessages());
};

export const reloadSystemBadges = () => (dispatch: AppDispatch) => {
    cache.remove(CACHE_KEYS.SYSTEM_BADGES);
    dispatch(fetchSystemBadges());
};
